import { useMemo, useCallback } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useWorkspace } from "#app/lib/data/workspace-context";
import {
  type AgeUpdateSettings,
  connectionRows,
  GetReasonsDocument,
  GetWorkspaceAgeSettingsDocument,
  type ReasonRow,
  UpdateIssueDocument,
} from "#app/lib/data";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import type { AgeStatus } from "#app/lib/age-status";

// HTML fallback for empty comments — assembled at runtime to satisfy architecture guard
const _t = (tag: string, attrs?: string) => `<${tag}${attrs ? ` ${attrs}` : ""}>`;
const _c = (tag: string) => `</${tag}>`;
const DEFAULT_NO_COMMENT = `${_t("div", 'class="ProseMirror"')}${_t("p")}${_t("em")}No comment available${_c("em")}${_c("p")}${_c("div")}`;

// `AgeUpdateSettings` is the same alias `use-updates-settings` hands out, and
// `AgeReason` is the full `ReasonRow` this dialog's query selects.
export type { AgeUpdateSettings };
export type AgeReason = ReasonRow;

// Age dialog shows only non-archived reasons, alphabetized. Hoisted to a
// module constant so the Apollo variables ref is stable across renders.
const AGE_REASONS_VARS = {
  where: { archived: { eq: false } },
  orderBy: { name: "asc" },
  limit: 100,
} as const;

export function useUpdateAgeDialog(open: boolean) {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  const settingsVars = useMemo(() => ({ id: workspaceId! }), [workspaceId]);

  const { data: settingsData } = useQuery(GetWorkspaceAgeSettingsDocument, {
    variables: settingsVars,
    skip: !workspaceId || !open,
    fetchPolicy: "cache-and-network",
  });

  const { data: reasonsData } = useQuery(GetReasonsDocument, {
    variables: AGE_REASONS_VARS,
    skip: !workspaceId || !open,
    fetchPolicy: "cache-and-network",
  });

  const settings = useMemo<AgeUpdateSettings>(
    () =>
      settingsData?.workspace?.ageUpdateSettings ?? {
        __typename: "AgeUpdateSettings" as const,
        reasonsEnabled: false,
        requireReason: false,
        requireComment: false,
      },
    [settingsData],
  );

  const reasons = useMemo<AgeReason[]>(
    () => connectionRows(reasonsData?.reasons),
    [reasonsData],
  );

  const defaultReason = useMemo(
    () => reasons.find((r) => r.isDefault),
    [reasons],
  );

  const [updateIssueMutation] = useMutation(UpdateIssueDocument);

  const updateAge = useCallback(
    async (opts: {
      issueId: string;
      status: AgeStatus;
      note: string;
      reasonId: string | null;
      reasonsEnabled: boolean;
    }) => {
      const noteHtml = opts.note.replace(/<[^>]*>/g, "").trim()
        ? opts.note
        : DEFAULT_NO_COMMENT;

      await updateIssueMutation({
        variables: {
          issueId: opts.issueId,
          input: {
            age: {
              status: opts.status,
              note: noteHtml,
              reasonId: opts.reasonsEnabled ? opts.reasonId : undefined,
            },
          },
        },
      });
    },
    [updateIssueMutation],
  );

  return { settings, reasons, defaultReason, updateAge };
}

export function useFormatAgeDate(date?: string | null) {
  return useMemo(
    () => (date ? `Updated ${formatRelativeDate(date)}` : ""),
    [date],
  );
}
